import { FileCode2, Printer } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { d } from '../lib/format';
import { exportInteractiveHtml } from '../lib/html-export';
import { useMe } from '../lib/me';
import { printReport } from '../lib/print';
import { Button, Modal, Notice } from './ui';

/**
 * The hosts of the reports (PDF by the print dialog, interactive HTML file): the report is rendered once into the page
 * (hidden on screen), printed or written into the file, then removed. Shared by the project analyses, the staff review,
 * the cockpit, the key figures of a project and the billing documents (round 5, request 16).
 */
export function ReportHost({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open || __DEMO__) return;
    // give the report one frame to render, then print and close
    const id = requestAnimationFrame(() => {
      printReport();
      onClose();
    });
    return () => cancelAnimationFrame(id);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      {createPortal(<div className="pz-report">{children}</div>, document.body)}
      {__DEMO__ && (
        <Modal
          open
          wide
          onClose={onClose}
          title={title}
          footer={
            <>
              <Button onClick={onClose}>{t('common.close')}</Button>
              <Button variant="primary" icon={<Printer className="size-4" />} onClick={printReport}>
                {t('analysis.printNow')}
              </Button>
            </>
          }
        >
          <Notice tone="warn" className="mb-3">
            {t('analysis.demoPrint')}
          </Notice>
          <div className="rounded-md border border-line bg-white p-4 text-[12px] text-[#17212b]">{children}</div>
        </Modal>
      )}
    </>
  );
}

export function ReportHeader({ title, sub, company }: { title: string; sub: string; company: string | null | undefined }) {
  const { me } = useMe();
  const { t } = useTranslation();
  return (
    <header className="flex items-end justify-between gap-4 border-b-2 border-[#17212b] pb-2">
      <div>
        <div className="text-[11px] text-graphite">{company}</div>
        <h1 className="text-lg font-semibold">{title}</h1>
        <div className="text-xs text-graphite">{sub}</div>
      </div>
      <div className="text-right text-[11px] text-graphite">
        {t('analysis.reportAsOf', { date: d(me.today) })}
        <br />
        {me.person.displayName}
      </div>
    </header>
  );
}

/** The interactive HTML file of a report (request 18 of round 4; round 5, request 16: in all exports for the client). */
export function HtmlReportHost({ open, onDone, title, fileName, children }: { open: boolean; onDone: () => void; title: string; fileName: string; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { me } = useMe();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      if (ref.current)
        exportInteractiveHtml({
          element: ref.current,
          title,
          fileName,
          lang: i18n.language,
          note: t('analysis.export.htmlNote', { date: d(me.today), name: me.person.displayName, app: me.settings.appName || 'Projektzeit' }),
        });
      onDone();
    });
    return () => cancelAnimationFrame(id);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  return createPortal(
    <div ref={ref} className="pz-report" aria-hidden>
      {children}
    </div>,
    document.body,
  );
}

/**
 * "HTML (interaktiv)" for a part of a page (round 5, request 16): the element as it is on screen, as one file that any
 * browser opens without an account (figures written on the charts, details when pointing). Buttons and filters marked
 * data-export-hide stay out. For the cockpit, the key figures of a project and similar views.
 */
export function ElementHtmlButton({ target, title, fileName }: { target: RefObject<HTMLElement | null>; title: string; fileName: string }) {
  const { t, i18n } = useTranslation();
  const { me } = useMe();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      data-export-hide
      icon={<FileCode2 className="size-3.5" />}
      loading={busy}
      title={t('analysis.export.htmlHint')}
      onClick={() => {
        if (!target.current) return;
        setBusy(true);
        try {
          exportInteractiveHtml({
            element: target.current,
            title,
            fileName,
            lang: i18n.language,
            note: t('analysis.export.htmlNote', { date: d(me.today), name: me.person.displayName, app: me.settings.appName || 'Projektzeit' }),
          });
        } finally {
          setBusy(false);
        }
      }}
    >
      {t('analysis.export.htmlShort')}
    </Button>
  );
}
